import { DuviNicheSitePageLayout } from "@/components/duvi-niche-site-page-layout";
import { DuviNicheSitePricingCard } from "@/components/duvi-niche-site-pricing-card";
import { DuviNicheSiteCtaBand } from "@/components/duvi-niche-site-cta-band";
import { DuviNicheSiteFaqList } from "@/components/duvi-niche-site-faq-list";
import type { DuviNicheSitePricingViewProps } from "./duvi-niche-site-pricing.types";

const TIER_GRID_CLASSES =
  "grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] items-stretch gap-[var(--grid-gap)] lg:gap-[var(--grid-gap-lg)]";

/**
 * DuviNicheSitePricingView — the pricing page, composed from the bespoke
 * Fishwalkers design system exactly as the brochure prototype's `#pricing`
 * artboard composes it (duvi-design/exports/niches/fishwalkers/brochure.zip):
 * a page head, the tier grid, the FAQ definition list, a closing CTA band.
 *
 * No payment form anywhere — signing up requires no payment yet.
 *
 * Pure function of props, and it holds no state at all: the FAQ is a
 * definition list, not an accordion, per the design system's static-friendly
 * constraint.
 */
export function DuviNicheSitePricingView({
  registerUrl,
  tiersState,
  faqs,
}: DuviNicheSitePricingViewProps) {
  return (
    <DuviNicheSitePageLayout registerUrl={registerUrl}>
      <section className="w-full bg-[var(--canvas)] pb-[var(--space-10)] pt-[var(--section-y)] lg:pt-[var(--section-y-lg)]">
        <div className="mx-auto flex w-full max-w-[var(--container-max)] flex-col gap-[var(--space-5)] px-[var(--container-pad)] lg:px-[var(--container-pad-lg)]">
          <h1 className="m-0 font-[family-name:var(--font-display)] text-[length:var(--text-display-sm)] font-extrabold leading-[var(--leading-display)] tracking-[var(--tracking-display)] text-[color:var(--heading)] text-pretty">
            Simple, flexible plans
          </h1>
          <p className="m-0 max-w-[var(--measure-lead)] font-[family-name:var(--font-body)] text-[length:var(--text-lead-sm)] leading-[var(--leading-lead)] text-[color:var(--body)] lg:text-[length:var(--text-lead)]">
            Choose the plan that fits your round — switch anytime, no payment
            required to start.
          </p>
        </div>
      </section>

      <section className="w-full bg-[var(--canvas)] pb-[var(--section-y)] lg:pb-[var(--section-y-lg)]">
        <div className="mx-auto w-full max-w-[var(--container-max)] px-[var(--container-pad)] lg:px-[var(--container-pad-lg)]">
          {tiersState.status === "loading" && (
            <div
              role="status"
              aria-label="Loading pricing"
              className={TIER_GRID_CLASSES}
            >
              {[0, 1, 2].map((placeholder) => (
                <div
                  key={placeholder}
                  aria-hidden="true"
                  className="h-[460px] animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface-soft)]"
                />
              ))}
            </div>
          )}

          {tiersState.status === "unavailable" && (
            <p className="m-0 max-w-[var(--measure-lead)] font-[family-name:var(--font-body)] text-[16px] leading-[var(--leading-body)] text-[color:var(--body)]">
              {"Pricing isn't available right now — please check back shortly."}
            </p>
          )}

          {tiersState.status === "ready" && (
            <div className={TIER_GRID_CLASSES}>
              {tiersState.tiers.map((tier) => (
                <DuviNicheSitePricingCard
                  key={tier.id}
                  tier={tier.displayName}
                  valueLine={tier.description}
                  price={tier.price}
                  period={tier.pricePeriod}
                  features={tier.features}
                  ctaLabel={`Sign up with ${tier.displayName}`}
                  ctaHref={tier.registerUrl}
                  highlighted={tier.highlighted}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <DuviNicheSiteFaqList
        heading="Questions walkers ask"
        items={faqs.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
        }))}
      />

      <DuviNicheSiteCtaBand
        heading="Not sure which plan? Start free and upgrade anytime."
        ctaLabel="Sign up free"
        ctaHref={registerUrl}
        tone="tint"
      />
    </DuviNicheSitePageLayout>
  );
}
